import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import Api from 'Vendor/Api';
import { UserContext } from 'Vendor/Context/UserContextProvider';
import EntityInterface from 'Vendor/Definition/EntityInterface';
import File from 'Entity/File';
import { User } from 'Components/Offcanvas/file/LikeSection';

interface LikeButtonProps {
    canEdit: boolean;
    changeStateOnEdit?: (status: boolean) => void;
    favoriesPropertySlug?: string;
    file: File | EntityInterface;
    updateLikes?: (email: string, name: string) => void;
    view?: "card" | "popup";
}

const _LikeButton: React.FC<LikeButtonProps> = ({ canEdit, changeStateOnEdit, favoriesPropertySlug, file, updateLikes, view = "popup" }) => {
    const [liked, setLiked] = useState<boolean>(false);
    const [likeCount, setLikeCount] = useState<number>(file?.likesCount || 0);
    const { currentUser } = useContext(UserContext);
    const favoriesPropertySlugValue = favoriesPropertySlug || (file.hasOwnProperty('favorisPropertyEnabled') && file.favorisPropertyEnabled?.slug);

    useEffect(() => {
        if (currentUser && favoriesPropertySlugValue && Array.isArray(file?.[favoriesPropertySlugValue])) {
            const status = file[favoriesPropertySlugValue].some(
                (element: User) => element.email === currentUser.email
            );
            setLiked(!!status);
        }
    }, [currentUser, favoriesPropertySlugValue, file]);


    let debounceTimeout: NodeJS.Timeout;

    const likeHandler = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!currentUser && !canEdit) return;

        setLiked((prev) => !prev);
        setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
        clearTimeout(debounceTimeout);

        debounceTimeout = setTimeout(async () => {
            try {
                await Api.post(`file/${file.id}/toggle-like`, { organization: currentUser?.currentOrganization });
            } catch (error) {
                console.error("Like action failed", error);
                setLiked((prev) => !prev);
                setLikeCount((prev) => (liked ? prev + 1 : prev - 1));
            } finally {
                updateLikes && updateLikes(currentUser?.email as string, currentUser?.firstName + ' ' + currentUser?.lastName);
                changeStateOnEdit && changeStateOnEdit(true);
            }
        }, 500);
    };

    const likeButton = (
        <motion.button
            onClick={likeHandler}
            className="popup-like-btn btn btn-icon btn-icon-only btn-icon-without-padding"
            whileTap={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
            disabled={!canEdit}
        >
            <i className={`fa-${liked ? "solid" : "regular"} fa-heart`}></i>
        </motion.button>
    );

    const likeButtonWithCount = (
        <motion.button
            onClick={likeHandler}
            className="btn btn-icon btn-icon-only btn-icon-without-padding more-actions like-btn d-flex align-items-center justify-content-center"
            whileTap={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
            disabled={!canEdit}
        >
            <i className={`fa-${liked ? "solid" : "regular"} fa-heart fs-5`}></i>
            <span className="fs-5">{likeCount}</span>
        </motion.button>
    );

    return view === "popup" && file?.favorisPropertyEnabled?.enabled ? likeButton : likeButtonWithCount;
};

export default _LikeButton;
